// Achievement Service
// Quản lý thành tích và gamification

import { sqliteDatabaseService } from '../database/SQLiteDatabaseService';
import { syncService } from '../sync/SyncService';
import { quizHistoryService } from './QuizHistoryService';
import { studyHistoryService } from './StudyHistoryService';
import {
  Achievement,
  CreateAchievementData,
  AchievementType,
  UserAction
} from '../../types/learningHistory';

// Achievement definitions
interface AchievementDefinition {
  type: AchievementType;
  title: string;
  description: string;
  icon: string;
  check: (userId: string, action?: UserAction) => Promise<boolean>;
}

class AchievementService {
  private static instance: AchievementService;
  
  // All available achievements
  private achievements: AchievementDefinition[] = [];

  private constructor() {
    this.initializeAchievements();
  }
  
  public static getInstance(): AchievementService {
    if (!AchievementService.instance) {
      AchievementService.instance = new AchievementService();
    }
    return AchievementService.instance;
  }

  // ==================== INITIALIZE ACHIEVEMENTS ====================

  private initializeAchievements(): void {
    this.achievements = [
      // First steps
      {
        type: 'first_quiz',
        title: 'Bước Đầu Tiên',
        description: 'Hoàn thành bài trắc nghiệm đầu tiên',
        icon: '🎯',
        check: async (userId: string) => {
          const quizStats = await quizHistoryService.getQuizStatistics(userId);
          return quizStats.totalQuizzes >= 1;
        }
      },
      
      {
        type: 'lesson_complete',
        title: 'Người Học Hỏi',
        description: 'Hoàn thành bài học đầu tiên',
        icon: '📚',
        check: async (userId: string) => {
          const progress = await studyHistoryService.getAllLessonProgress(userId);
          return progress.some(p => p.status === 'completed');
        }
      },

      // Perfect scores
      {
        type: 'perfect_score',
        title: 'Hoàn Hảo',
        description: 'Đạt 100% trong một bài trắc nghiệm',
        icon: '💯',
        check: async (userId: string, action?: UserAction) => {
          if (action?.type === 'complete_quiz' && action.metadata?.score === 100) {
            return true;
          }
          return false;
        }
      },

      // Speed achievements
      {
        type: 'speedster',
        title: 'Tốc Độ Ánh Sáng',
        description: 'Hoàn thành bài trắc nghiệm dưới 50% thời gian giới hạn',
        icon: '⚡',
        check: async (userId: string, action?: UserAction) => {
          if (action?.type === 'complete_quiz' && action.metadata?.timeSpent) {
            const timeSpent = action.metadata.timeSpent;
            const timeLimit = action.metadata.timeLimit || 900; // Default 15 minutes
            return timeSpent < timeLimit * 0.5;
          }
          return false;
        }
      },

      // Volume achievements
      {
        type: 'quiz_master',
        title: 'Bậc Thầy Trắc Nghiệm',
        description: 'Hoàn thành 50 bài trắc nghiệm',
        icon: '🎓',
        check: async (userId: string) => {
          const quizStats = await quizHistoryService.getQuizStatistics(userId);
          return quizStats.totalQuizzes >= 50;
        }
      },

      {
        type: 'chapter_complete',
        title: 'Chinh Phục Chương',
        description: 'Hoàn thành tất cả bài học trong một chương',
        icon: '📖',
        check: async (userId: string, action?: UserAction) => {
          // Check if user completed all lessons in a chapter
          // This would require chapter metadata
          return false; // Placeholder - implement based on your chapter structure
        }
      },

      // Streak achievements
      {
        type: 'streak_master',
        title: 'Kiên Trì',
        description: 'Học liên tục 7 ngày',
        icon: '🔥',
        check: async (userId: string) => {
          const stats = await studyHistoryService.getUserStatistics(userId);
          return stats.currentStreak >= 7;
        }
      },

      // Time-based achievements
      {
        type: 'early_bird',
        title: 'Chim Sớm',
        description: 'Học trước 8 giờ sáng',
        icon: '🌅',
        check: async (userId: string, action?: UserAction) => {
          if (action?.timestamp) {
            const hour = action.timestamp.getHours();
            return hour < 8;
          }
          return false;
        }
      },

      {
        type: 'night_owl',
        title: 'Cú Đêm',
        description: 'Học sau 10 giờ tối',
        icon: '🦉',
        check: async (userId: string, action?: UserAction) => {
          if (action?.timestamp) {
            const hour = action.timestamp.getHours();
            return hour >= 22;
          }
          return false;
        }
      },

      // Improvement achievements
      {
        type: 'improver',
        title: 'Tiến Bộ Vượt Bậc',
        description: 'Cải thiện điểm số 20 điểm so với lần trước',
        icon: '📈',
        check: async (userId: string, action?: UserAction) => {
          if (action?.type === 'complete_quiz' && action.metadata?.quizId) {
            const attempts = await quizHistoryService.getAllAttempts(
              userId,
              action.metadata.quizId
            );
            
            if (attempts.length >= 2) {
              const latest = attempts[attempts.length - 1];
              const previous = attempts[attempts.length - 2];
              const improvement = latest.score - previous.score;
              return improvement >= 20;
            }
          }
          return false;
        }
      }
    ];

    console.log(`🏆 Initialized ${this.achievements.length} achievement types`);
  }

  // ==================== CHECK & AWARD ====================

  /**
   * Kiểm tra và trao achievements dựa trên action của user
   */
  async checkAndAwardAchievements(userId: string, action: UserAction): Promise<Achievement[]> {
    console.log(`🔍 Checking achievements for user: ${userId}, action: ${action.type}`);

    const newAchievements: Achievement[] = [];

    // Check each achievement
    for (const achievementDef of this.achievements) {
      // Skip if user already has this achievement
      const hasAchievement = await sqliteDatabaseService.hasAchievement(
        userId,
        achievementDef.type
      );

      if (hasAchievement) {
        continue;
      }

      // Check if user qualifies for this achievement
      try {
        const qualifies = await achievementDef.check(userId, action);

        if (qualifies) {
          console.log(`🏆 User qualifies for achievement: ${achievementDef.title}`);

          const achievement = await this.unlockAchievement(
            userId,
            achievementDef.type,
            achievementDef.title,
            achievementDef.description,
            achievementDef.icon,
            action.metadata
          );

          newAchievements.push(achievement);
        }
      } catch (error) {
        console.error(`Error checking achievement ${achievementDef.type}:`, error);
      }
    }

    if (newAchievements.length > 0) {
      console.log(`🎉 Awarded ${newAchievements.length} new achievements!`);
    }

    return newAchievements;
  }

  /**
   * Trao thành tích cho user
   */
  async unlockAchievement(
    userId: string,
    type: AchievementType,
    title?: string,
    description?: string,
    icon?: string,
    metadata?: any
  ): Promise<Achievement> {
    console.log(`🏆 Unlocking achievement: ${type} for user: ${userId}`);

    // Find achievement definition if title not provided
    const achievementDef = this.achievements.find(a => a.type === type);

    const achievementData: CreateAchievementData = {
      userId,
      achievementType: type,
      title: title || achievementDef?.title || 'Achievement',
      description: description || achievementDef?.description || '',
      icon: icon || achievementDef?.icon || '🏆',
      metadata
    };

    const achievement = await sqliteDatabaseService.createAchievement(achievementData);
    
    // Add to sync queue
    syncService.addToQueue({
      type: 'achievement',
      action: 'create',
      data: achievement
    });
    
    console.log(`✅ Achievement unlocked: ${achievement.title}`);
    
    return achievement;
  }

  // ==================== RETRIEVE ====================

  /**
   * Lấy tất cả achievements của user
   */
  async getUserAchievements(userId: string): Promise<Achievement[]> {
    return await sqliteDatabaseService.getUserAchievements(userId);
  }

  /**
   * Lấy achievements gần đây
   */
  async getRecentAchievements(userId: string, limit: number = 5): Promise<Achievement[]> {
    const allAchievements = await this.getUserAchievements(userId);
    
    // Sort by earnedAt (most recent first)
    allAchievements.sort((a, b) => 
      new Date(b.earnedAt).getTime() - new Date(a.earnedAt).getTime()
    );

    return allAchievements.slice(0, limit);
  }

  /**
   * Kiểm tra xem user đã có achievement chưa
   */
  async hasAchievement(userId: string, type: AchievementType): Promise<boolean> {
    return await sqliteDatabaseService.hasAchievement(userId, type);
  }

  /**
   * Lấy tất cả achievement definitions (để hiển thị locked achievements)
   */
  getAllAchievementDefinitions(): AchievementDefinition[] {
    return this.achievements;
  }

  /**
   * Lấy thông tin về 1 achievement definition
   */
  getAchievementDefinition(type: AchievementType): AchievementDefinition | undefined {
    return this.achievements.find(a => a.type === type);
  }

  // ==================== PROGRESS TRACKING ====================

  /**
   * Lấy tiến độ của user với từng achievement (bao nhiêu % đạt được)
   */
  async getAchievementProgress(userId: string): Promise<Map<AchievementType, number>> {
    const progress = new Map<AchievementType, number>();

    // For quiz_master: count / 50
    const quizStats = await quizHistoryService.getQuizStatistics(userId);
    progress.set('quiz_master', Math.min(100, (quizStats.totalQuizzes / 50) * 100));

    // For streak_master: currentStreak / 7
    const userStats = await studyHistoryService.getUserStatistics(userId);
    progress.set('streak_master', Math.min(100, (userStats.currentStreak / 7) * 100));

    // For lesson_complete: check if any completed
    const allProgress = await studyHistoryService.getAllLessonProgress(userId);
    const hasCompletedLesson = allProgress.some(p => p.status === 'completed');
    progress.set('lesson_complete', hasCompletedLesson ? 100 : 0);

    // For first_quiz: check if any quiz taken
    progress.set('first_quiz', quizStats.totalQuizzes > 0 ? 100 : 0);

    // Other achievements are binary (0% or 100%)
    const hasAchievements = await this.getUserAchievements(userId);
    const achievementTypes = hasAchievements.map(a => a.achievementType);

    for (const type of achievementTypes) {
      if (!progress.has(type)) {
        progress.set(type, 100);
      }
    }

    return progress;
  }

  // ==================== HELPER: TRIGGER ACHIEVEMENT CHECKS ====================

  /**
   * Helper method: Check achievements after completing a quiz
   */
  async checkAfterQuizComplete(
    userId: string,
    quizId: string,
    score: number,
    timeSpent: number,
    timeLimit?: number,
    perfect?: boolean
  ): Promise<Achievement[]> {
    const action: UserAction = {
      type: 'complete_quiz',
      userId,
      timestamp: new Date(),
      metadata: {
        quizId,
        score,
        timeSpent,
        timeLimit,
        perfect: perfect || score === 100
      }
    };

    return await this.checkAndAwardAchievements(userId, action);
  }

  /**
   * Helper method: Check achievements after completing a lesson
   */
  async checkAfterLessonComplete(
    userId: string,
    lessonId: string,
    timeSpent: number
  ): Promise<Achievement[]> {
    const action: UserAction = {
      type: 'complete_lesson',
      userId,
      timestamp: new Date(),
      metadata: {
        lessonId,
        timeSpent
      }
    };

    return await this.checkAndAwardAchievements(userId, action);
  }

  /**
   * Helper method: Check achievements after study session
   */
  async checkAfterStudySession(
    userId: string,
    duration: number
  ): Promise<Achievement[]> {
    const action: UserAction = {
      type: 'study_session',
      userId,
      timestamp: new Date(),
      metadata: {
        duration
      }
    };

    return await this.checkAndAwardAchievements(userId, action);
  }

  /**
   * Helper method: Check achievements on login (for streak)
   */
  async checkOnLogin(userId: string): Promise<Achievement[]> {
    const action: UserAction = {
      type: 'login',
      userId,
      timestamp: new Date()
    };

    return await this.checkAndAwardAchievements(userId, action);
  }
}

export const achievementService = AchievementService.getInstance();
export default AchievementService;

